import { useFormik } from "formik";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@mui/material";
import currency from "currency.js";
import { useAxios } from "../../../../hooks";
import { DateRangePicker, Input, Paper, Typography } from "../../../../components";
import { formatMoney } from "../../../../utils";
import AuthContext from "../../../../context/auth";

function getDatesBetween(startDate: Date, endDate: Date) {
  const dates = [];
  let currentDate = moment(startDate);
  const finalDate = moment(endDate);

  while (currentDate.isSameOrBefore(finalDate)) {
    dates.push(currentDate.format('DD/MM/YYYY'));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

function getMonthsBetween(startDate: Date, endDate: Date) {
  const months = [];
  let current = moment(startDate).startOf('month');
  const final = moment(endDate).startOf('month');

  while (current.isSameOrBefore(final)) {
    months.push(current.format('MM/YYYY'));
    current.add(1, 'month');
  }

  return months;
}


export const BonusPastTwelveMonths = () => {
  const theme = useTheme()
  const { me } = useContext(AuthContext)
  const [dashboard, refetchDashboard] = useAxios(`/bonus?user_id=${me?.id}`, {
    manual: true,
  });

  const [vision, setVision] = useState<'daily' | 'monthly'>('monthly')

  const searchFormik = useFormik({
    initialValues: {
      from: moment().subtract(12, "month").toDate(),
      to: moment().toDate(),
      user_id: [] as Array<string | number>,
    },
    onSubmit: (values) => {},
  });

  const values = searchFormik.values;

  const graph = useMemo(() => {
    const base = vision === 'daily' ? getDatesBetween(values.from, values.to) : getMonthsBetween(values.from, values.to)

    const res = base.map(key => {
      const profit = (dashboard.data || []).reduce((prev: number, curr: { order: { created_at: Date }, amount: number }) => {
        const currKey = moment(curr.order.created_at).format(vision === 'daily' ? 'DD/MM/YYYY' : 'MM/YYYY')
        if (currKey === key) {
          return currency(prev).add(Number(curr.amount)).value
        }

        return prev
      }, 0)

      return {
        key,
        profit: profit || 0
      }
    })

    return res
  }, [dashboard.data, vision])

  useEffect(() => {
    refetchDashboard({
      params: {
        from: values.from,
        to: values.to
      },
    });
  }, [values]);

  return (
    <Paper
      elevation={1}
      sx={{
        height: "100%",
        padding: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: 10,
          alignItems: "center",
        }}
      >
        <Typography
          component="h2"
          variant="h6"
          color="primary"
          fontSize={20}
          flex={1}
        >
          Comissões
        </Typography>
        <div
          style={{
            display: "flex",
            flex: 2,
            justifyContent: "space-between",
            gap: 1
          }}
        >
          <div style={{display: 'flex', flex: 1}}>
            <Input
              options={[
                {label: 'Mensal', value: 'monthly'},
                {label: 'Diário', value: 'daily'},
              ]}
              value={vision}
              onChange={({target}) => {
                setVision(target.value as 'monthly')
              }}
              select
            />
          </div>
          <div style={{display: 'flex', flex: 2}}>
          <DateRangePicker
            startDate={searchFormik.values.from}
            endDate={searchFormik.values.to}
            onChange={(value) => {
              searchFormik.setFieldValue("from", value.startDate);
              searchFormik.setFieldValue("to", value.endDate);
            }}
          />
          </div>
        </div>
      </div>
      <div
        style={{
          height: "100%",
        }}
      >
        <ResponsiveContainer height={"90%"}>
          {!dashboard.loading ? (
            <LineChart
              data={graph || []}
              margin={{
                top: 16,
                right: 16,
                bottom: 0,
                left: 16,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="key"
                includeHidden
                stroke={theme.palette.text.secondary}
                style={theme.typography.body2}
                // tickFormatter={(tick) => {
                //   return capitalizeFirstLetter(
                //     moment(`${tick}/01`).format("MMM/YYYY")
                //   );
                // }}
                tickLine
                tickCount={12}
              ></XAxis>
              <YAxis
                stroke={theme.palette.text.secondary}
                style={theme.typography.body2}
                tickFormatter={(tick) => {
                  return formatMoney(tick);
                }}
                tick
              ></YAxis>
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="profit"
                stroke={theme.palette.primary.main}
              />
              <Tooltip
                formatter={(value: number) => {
                  return [formatMoney(value)];
                }}
                // labelFormatter={(label) => {
                //   return capitalizeFirstLetter(
                //     moment(`${label}/01`).format("MMMM/YYYY")
                //   );
                // }}
                labelStyle={{
                  fontWeight: "bold",
                }}
              />
              <Legend
                formatter={() => {
                  return "Comissão";
                }}
                fontWeight="bold"
              />
            </LineChart>
          ) : (
            <div />
          )}
        </ResponsiveContainer>
      </div>
    </Paper>
  );
};
